/**
 * Replay identity lifecycle.
 *
 * A replay = one page-load's worth of events, continued across SPA navigation
 * (same recorder instance). Each hard page load mints a NEW replay id, chained
 * to the previous one via `continuedFromReplayId` **only when it's the same
 * user in the same tab** — so a hard reload continues the story, but an
 * impersonation swap or a different-user login starts a clean, unchained replay
 * (their session identity differs). This also sidesteps chunk-seq collisions:
 * every id is unique, so its chunks always start at seq 0.
 */

const STATE_KEY = "rawiah.replay.state";
const TAB_KEY = "rawiah.replay.tab";

export interface ReplayState {
  replayId: string;
  userId: string;
  tabId: string;
  startedAt: number; // epoch ms
  continuedFromReplayId?: string;
}

function rid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function safeGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage disabled — replay just won't chain across reloads */
  }
}

function loadState(): ReplayState | null {
  const raw = safeGet(STATE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ReplayState;
  } catch {
    return null;
  }
}

function saveState(s: ReplayState): void {
  safeSet(STATE_KEY, JSON.stringify(s));
}

/** Stable per-tab id (survives SPA nav + reload within the tab). */
function tabId(): string {
  let t = safeGet(TAB_KEY);
  if (!t) {
    t = rid("tab");
    safeSet(TAB_KEY, t);
  }
  return t;
}

/**
 * Mint a fresh replay for `userId`, chaining from the previous replay when it
 * belonged to the same user in the same tab.
 */
export function startNewReplay(userId: string): ReplayState {
  const prev = loadState();
  const tid = tabId();
  const continuedFromReplayId =
    prev && prev.userId === userId && prev.tabId === tid
      ? prev.replayId
      : undefined;
  const next: ReplayState = {
    replayId: rid("rep"),
    userId,
    tabId: tid,
    startedAt: Date.now(),
    ...(continuedFromReplayId ? { continuedFromReplayId } : {}),
  };
  saveState(next);
  return next;
}

/** Forget the persisted replay so the next start is a clean, unchained one.
 *  Called on logout + before impersonation swaps. */
export function clearReplayState(): void {
  try {
    sessionStorage.removeItem(STATE_KEY);
  } catch {
    /* ignore */
  }
}
